/**
 * Refine an asset with AI: what should change (Markdown, required) and, for readers of internal content (the server
 * requires it), files to read. Queuing opens the review page; nothing changes on the asset until the person applies.
 * A refusal (two refinements running, an archived asset) stays in the dialog with the instruction kept.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WandSparkles } from 'lucide-react';

import { RefinementIn, refinementInInstructionMax } from '@/api/generated/schemas';
import { useStartRefinement } from '@/api/queries';
import type { AssetOut } from '@/api/types/assets';
import { bannerError, fieldErrors, serverFieldErrors } from '@/api/validate';
import { useSession } from '@/session/SessionContext';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { CharCount } from '@/ui/CharCount';
import { MarkdownEditor } from '@/ui/MarkdownEditor';
import { Modal } from '@/ui/Modal';
import { FormError } from '@/ui/StateCard';

import { FilesPicker, filesProblem, type MaterialUploads } from '../files/FilesPicker';
import { useMaterialUploads } from '../materials/useMaterialUploads';
import { keepInstruction } from './refineDraft';

function Files({ uploads }: { uploads: MaterialUploads }) {
  return (
    <div className="field">
      <span className="hint">Files to read (optional): notes, a deck or a document the AI should take the changes from.</span>
      <FilesPicker uploads={uploads} id="refine-files" />
    </div>
  );
}

export function RefineAssetDialog({ asset, initial = '', onClose }: { asset: AssetOut; initial?: string; onClose: () => void }) {
  const navigate = useNavigate();
  const { can, user } = useSession();
  const start = useStartRefinement(asset.id);
  const uploads = useMaterialUploads();
  const [instruction, setInstruction] = useState(initial);
  const withFiles = can('asset.view.internal');
  const fileIds = uploads.landed.map((l) => l.file.id);
  const problem = filesProblem(uploads.landed.map((l) => uploads.statusOf(l.file)), uploads.draft.busy);
  const invalid = Object.keys(fieldErrors(RefinementIn, { instruction, file_ids: fileIds })).length > 0;

  function onSubmit() {
    if (start.isPending || invalid || problem) return;
    const text = instruction.trim();
    start.mutate(
      { instruction: text, file_ids: fileIds },
      {
        onSuccess: (answer) => {
          keepInstruction(user?.id ?? 'anonymous', answer.id, text);
          navigate(`/assets/${asset.id}/refine/${answer.id}`);
        },
      },
    );
  }

  return (
    <Modal
      title={`Refine ${asset.name}`}
      subtitle="Say what should change. The AI proposes changes to the fields and sections, and you choose which to apply."
      size="wide"
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <div className="submit-actions">
          <span className="hint">Uses AI budget. Nothing changes until you apply.</span>
          <span className="row">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <ActionButton
              state={buttonState(start)}
              icon={<WandSparkles aria-hidden="true" />}
              label="Refine"
              pendingLabel="Queuing…"
              doneLabel="Queued"
              disabled={invalid || problem !== null}
            />
          </span>
        </div>
      }
    >
      <div className="stack-md">
        <FormError error={bannerError(start.error, ['instruction'])} />
        <div className="field">
          <label htmlFor="refine-instruction">What should change?</label>
          <MarkdownEditor
            id="refine-instruction"
            rows={7}
            maxLength={refinementInInstructionMax}
            value={instruction}
            placeholder="For example: tighten the summary for a CFO, add the new Azure partnership, or rewrite Steps from the attached deck."
            describedBy="refine-instruction-count"
            error={serverFieldErrors(start.error).instruction}
            onChange={setInstruction}
          />
          <CharCount id="refine-instruction-count" value={instruction} max={refinementInInstructionMax} />
        </div>
        {withFiles && <Files uploads={uploads} />}
        {withFiles && problem && <p className="hint" aria-live="polite">{problem}</p>}
      </div>
    </Modal>
  );
}
